export interface WindowFirmaOpenOptions {
  payload: unknown;
  src: string;
  domain: string;
  windowStyle?: Partial<WindowFirmaConfig>;
  blockerMessage?: string;
  useBlocker?: boolean;
  showForceCloseButton?: boolean;
  forceCloseButtonText?: string;
}

export interface WindowFirmaResponse {
  payload: any;
  type?: string;
}

export interface WindowFirmaFeatures {
  toolbar?: 'yes' | 'no';
  menubar?: 'yes' | 'no';
  scrollbars?: 'yes' | 'no';
  resizable?: 'yes' | 'no';
  location?: 'yes' | 'no';
  status?: 'yes' | 'no';
  popup?: 'yes' | 'no';
  [key: string]: string | number | undefined;
}

export interface WindowFirmaConfig {
  name: string;
  width: number;
  height: number;
  center: boolean;
  features: WindowFirmaFeatures;
}

export class WindowFirma {
  private win: Window | null = null;
  private channel: MessageChannel | null = null;
  private ready = true;
  private blockerEl: HTMLDivElement | null = null;
  private blockerMessageEl: HTMLDivElement | null = null;
  private pendingResolver: ((value: WindowFirmaResponse) => void) | null = null;

  private readonly pingIntervalMs = 500;

  private readonly defaults: WindowFirmaConfig = {
    name: 'firmaWindow',
    width: 900,
    height: 500,
    center: true,
    features: {
      toolbar: 'no',
      menubar: 'no',
      scrollbars: 'no',
      resizable: 'no',
      location: 'no',
      status: 'no',
      popup: 'yes',
    },
  };

  private buildFeatures(config: WindowFirmaConfig): string {
    const { width, height, center, features } = config;

    let left = 0;
    let top = 0;

    if (center) {
      left = Math.max(0, (window.screen.width - width) / 2);
      top = Math.max(0, (window.screen.height - height) / 2);
    }

    const featureObject: Record<string, string | number> = {
      width,
      height,
      left: Math.floor(left),
      top: Math.floor(top),
      ...features,
    };

    return Object.entries(featureObject)
      .map(([k, v]) => `${k}=${v}`)
      .join(',');
  }

  open(options: WindowFirmaOpenOptions): Promise<WindowFirmaResponse> {
    const {
      payload,
      src,
      domain,
      windowStyle,
      blockerMessage,
      useBlocker,
      showForceCloseButton,
      forceCloseButtonText,
    } = options;
    const shouldUseBlocker = useBlocker ?? true;
    const message = blockerMessage ?? 'Procesando firma...';
    const shouldShowForceCloseButton = showForceCloseButton ?? true;
    const resolvedForceCloseButtonText = forceCloseButtonText ?? 'Forzar cierre';

    if (!this.ready && this.win && !this.win.closed) {
      this.win.focus();
      return Promise.reject('La ventana de firma ya está abierta');
    }

    this.ready = false;
    if (shouldUseBlocker) {
      this.showBlocker(message, shouldShowForceCloseButton, resolvedForceCloseButtonText);
    }

    const config: WindowFirmaConfig = {
      ...this.defaults,
      ...windowStyle,
      features: {
        ...this.defaults.features,
        ...(windowStyle?.features || {}),
      },
    };

    const features = this.buildFeatures(config);

    this.win = window.open(src, config.name, features);

    if (!this.win) {
      this.close();
      return Promise.reject('Popup bloqueado por el navegador');
    }

    this.channel = new MessageChannel();

    return new Promise((resolve, reject) => {
      if (!this.channel) {
        this.close();
        reject('No se pudo crear el canal de comunicación');
        return;
      }

      this.pendingResolver = resolve;

      let pingIntervalId: number | null = null;
      let requestSent = false;

      const handleWindowMessage = (event: MessageEvent): void => {
        if (event.source !== this.win) {
          return;
        }

        if (event.data?.type !== 'firmaReady') {
          return;
        }

        if (domain !== '*' && event.origin !== domain) {
          return;
        }

        sendRequest();
      };

      const cleanupTimers = (): void => {
        if (pingIntervalId !== null) {
          window.clearInterval(pingIntervalId);
        }

        window.removeEventListener('message', handleWindowMessage);
      };

      const sendRequest = (): void => {
        if (requestSent || !this.win || this.win.closed || !this.channel) {
          return;
        }

        requestSent = true;

        try {
          this.win.postMessage(
            {
              type: 'getInfoFirma',
              payload,
            },
            domain,
            [this.channel.port2]
          );

          if (shouldUseBlocker) {
            this.showBlocker(message, shouldShowForceCloseButton, resolvedForceCloseButtonText);
          }

          if (pingIntervalId !== null) {
            window.clearInterval(pingIntervalId);
            pingIntervalId = null;
          }

        } catch {
          requestSent = false;
        }
      };

      this.channel.port1.onmessage = (event: MessageEvent) => {
        if (event.data?.type && event.data.type !== 'ResponseFirma') {
          return;
        }
        cleanupTimers();
        this.pendingResolver = null;
        resolve(event.data);
        this.close();
      };

      this.channel.port1.start();

      window.addEventListener('message', handleWindowMessage);

      pingIntervalId = window.setInterval(() => {
        if (!this.win || this.win.closed) {
          cleanupTimers();
          this.pendingResolver = null;
          this.close();
          resolve({
            type: 'ResponseFirma',
            payload: {
              mensaje: 'La ventana fue cerrada antes de terminar el proceso',
              codigo: -1,
              success: false,
              idOperacion: null,
              idProceso: null,
              _windowClosed: true,
            }
          });
          return;
        }

        try {
          this.win.postMessage({ type: 'firmaPing' }, domain);
        } catch {
          // la ventana aún no está lista
        }
      }, this.pingIntervalMs);
    });
  }

  close(): void {
    try {
      this.win?.close();
    } catch {
      // ignore
    }

    this.channel?.port1.close();

    this.win = null;
    this.channel = null;
    this.ready = true;
    this.hideBlocker();
  }

  private forceCloseFromBlocker(): void {
    this.pendingResolver?.({
      type: 'ResponseFirma',
      payload: {
        mensaje: 'Proceso cancelado manualmente por el usuario',
        codigo: -1,
        success: false,
        idOperacion: null,
        idProceso: null,
        _windowClosed: true,
        _forcedClose: true,
      },
    });
    this.pendingResolver = null;
    this.close();
  }

  private showBlocker(
    message: string,
    showForceCloseButton: boolean,
    forceCloseButtonText: string,
  ): void {
    if (typeof document === 'undefined') return;

    if (!this.blockerEl) {
      const overlay = document.createElement('div');
      overlay.setAttribute('data-window-firma-blocker', 'true');
      overlay.style.position = 'fixed';
      overlay.style.inset = '0';
      overlay.style.zIndex = '2500';
      overlay.style.display = 'flex';
      overlay.style.alignItems = 'center';
      overlay.style.justifyContent = 'center';
      overlay.style.background = 'rgba(2, 6, 23, 0.52)';

      const card = document.createElement('div');
      card.style.display = 'grid';
      card.style.gap = '10px';
      card.style.background = '#ffffff';
      card.style.border = '1px solid #cbd5e1';
      card.style.borderRadius = '12px';
      card.style.padding = '12px 16px';
      card.style.fontFamily = 'var(--font-family-base, Arial, sans-serif)';
      card.style.fontWeight = '600';
      card.style.color = '#0f172a';
      card.style.maxWidth = '90vw';
      card.style.textAlign = 'center';

      const messageEl = document.createElement('div');
      messageEl.style.lineHeight = '1.4';

      card.appendChild(messageEl);
      if (showForceCloseButton) {
        const forceCloseBtn = document.createElement('button');
        forceCloseBtn.type = 'button';
        forceCloseBtn.textContent = forceCloseButtonText;
        forceCloseBtn.style.padding = '8px 12px';
        forceCloseBtn.style.border = '1px solid var(--color-primary, #161a1d)';
        forceCloseBtn.style.borderRadius = '8px';
        forceCloseBtn.style.background = 'var(--color-secondary, #575f71)';
        forceCloseBtn.style.color = 'var(--color-surface, #ffffff)';
        forceCloseBtn.style.cursor = 'pointer';
        forceCloseBtn.style.fontFamily = 'inherit';
        forceCloseBtn.style.fontSize = 'var(--font-size-base, 14px)';
        forceCloseBtn.style.fontWeight = '600';
        forceCloseBtn.style.justifySelf = 'center';
        forceCloseBtn.addEventListener('click', () => this.forceCloseFromBlocker());

        card.appendChild(forceCloseBtn);
      }

      overlay.appendChild(card);
      document.body.appendChild(overlay);
      this.blockerEl = overlay;
      this.blockerMessageEl = messageEl;
    }

    if (this.blockerMessageEl) {
      this.blockerMessageEl.textContent = message;
    }
  }

  private hideBlocker(): void {
    this.blockerEl?.remove();
    this.blockerEl = null;
    this.blockerMessageEl = null;
  }
}
